import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store/indexRedux";
import { fetchPartners } from "../../store/partnerSlice";
import DataIcon from "../svg/DataIcon";
import Spinner from "../ui/Spinner";
import SearchInput from "../ui/SearchInput";
import MultiSelectDropdown from "../ui/MultiSelectDropdown";
import WarningIcon from "../svg/WarningIcon";
import SearchIcon from "../svg/SearchIcon";
import ServiceDetailsModal from "./ServiceDetailsModal";
import ReturnArrowIcon from "../svg/ReturnArrowIcon";
import { FlexService, InjectionService, OfftakeService } from "../../types/portfolioTypes";
import { getFlexContractStatus } from "../../functions/flexServices";
import { eocBadgeOutcome } from "../../utils/eocStatus";
import { useContractStatuses } from "../../hooks/useContractStatuses";
import { needsCommoditySignature } from "../../utils/commoditySignability";
import { ContractStatusChip } from "./ContractStatusChip";
import { useNavigate } from "react-router-dom";
import { DigitalSignatureModal } from "../addServices/DigitalSignatureModal";
import { BillingInfo } from "../../types/addServiceTypes";
import { getSessionFromCookie } from "../utils/cognito";
import { useRoles } from "../../hooks/useRoles";
import axios from "axios";
import SignatureIcon from "../svg/SignatureIcon";
import SendIcon from "../svg/SendIcon";
import { SendProposalModal } from "./SendProposalModal";
import BessResignBanner from "../bessResign/BessResignBanner";
import { BessResignContractModal } from "../bessResign/BessResignContractModal";
import { regenerateBessResignPdf, BessResignError } from "../../api/bessResign";
import {
  selectPendingBessResigns,
  fetchPendingBessResignsThunk,
} from "../../store/bessResignSlice";
import type {
  BessResignPendingRow,
  BessResignRegenerateBillingInfo,
} from "../../types/bessResignTypes";
import ArrowLongRightIcon from "../svg/ArrowLongRightIcon";
import { resetStep1, setInjectionSelection, PV_STORAGE_KEYS } from "../../store/pvCurtailment";
import { SuccessToast } from "../ui/toastResponds/SuccessToast";
import { Bounce, toast, ToastContainer } from "react-toastify";
import { ErrorToast } from "../ui/toastResponds/ErrorToast";
import { ContractErrorBody, resolveContractErrorCopy } from "../../utils/contractErrorCodes";
import { PendingToast } from "../ui/toastResponds/PendingToast";
import { toastConfig } from "../config/toast.config";
import * as Sentry from "@sentry/react";
import {
  ContractPreviewModal,
  ContractSigningPayload,
  PvCurtailmentContractData,
} from "../pvcurtailment/ContractPreviewModal";
import {
  ContractPreviewModal as BessContractPreviewModal,
  BessOnboardingContractData,
} from "../bessOnboarding/ContractPreviewModal";
import { fetchPortfolioServices, selectBessServices, selectEboilerServices } from "../../store/companyPortfolioSlice";
import { getFirstOfMonthOnOrAfter } from "../../utils/dateUtils";
import { insideServiceUrl } from "../utils/insideLink";

type ServiceType = InjectionService | OfftakeService | FlexService;

interface GroupedService {
  type: string;
  data: ServiceType;
}

interface SiteGroup {
  siteKey: string;
  siteName: string;
  services: GroupedService[];
}

function getFullAddress(service: InjectionService | OfftakeService): string {
  const street = service.addressStreet || "";
  const number = service.addressNumber || "";
  const zip = service.addressZip || "";
  const city = service.addressCity || "";

  const streetPart = [street, number].filter(Boolean).join(" ");
  const zipCity = [zip, city].filter(Boolean).join(" ");
  const parts = [streetPart, zipCity].filter(Boolean).join(", ");

  return parts || "-";
}

function getFlexAddress(service: FlexService): string {
  const street = service.address_street || "";
  const number = service.address_number || "";
  const zip = service.address_zip || "";
  const city = service.address_city || "";

  const streetPart = [street, number].filter(Boolean).join(" ");
  const zipCity = [zip, city].filter(Boolean).join(" ");
  return [streetPart, zipCity].filter(Boolean).join(", ") || "-";
}

function findInjectionAtAddress(
  service: FlexService,
  injections: InjectionService[]
): InjectionService | undefined {
  const flexAddr = getFlexAddress(service);
  return injections.find((inj) => getFullAddress(inj) === flexAddr);
}

// product_name now exists on commodity rows too (FT-4991); the flex SDP id is
// the reliable flex-only marker.
const isFlexService = (service: ServiceType): service is FlexService => "sdp_flex_id" in service;

const isInjectionService = (service: ServiceType): service is InjectionService =>
  "ean" in service && "power" in service && !isFlexService(service);

const isOfftakeService = (service: ServiceType): service is OfftakeService =>
  "ean" in service && "peakpower" in service && !isFlexService(service);

const PortfolioServices: React.FC = () => {
  const { t } = useTranslation("management");
  // The send-proposal copy lives in the ActiveServices namespace alongside SendProposalModal.
  const { t: tActiveServices } = useTranslation("ActiveServices");
  const { data, loading } = useSelector((state: RootState) => state.companyPortfolio);
  const [expandedSites, setExpandedSites] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);

  const selectedUserCompany = useSelector(
    (state: RootState) => (state.selectedCompany as { selectedUserCompany: { company_id: number; companyName: string; companyNumber: string; vatCode: string } | null }).selectedUserCompany,
  );
  const user = useSelector((state: RootState) => state.user);
  const { isAdminOrPartner } = useRoles();
  const canSendProposal = isAdminOrPartner;
  const { partners, status: partnerStatus } = useSelector((state: RootState) => state.partner);
  const accessToken = useSelector((state: RootState) => state.authv2.accessToken);
  const bessServices = useSelector(selectBessServices) ?? [];
  // FT-1189: server-computed per-SDP statuses; legacy derivation stays the fallback
  const { statuses: sdpStatuses } = useContractStatuses(selectedUserCompany?.company_id);
  const eboilerServices = useSelector(selectEboilerServices) ?? [];
  const dispatch = useDispatch();

  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureModalData, setSignatureModalData] = useState<{
    pdfUrls: { offtake: string | null; injection: string | null };
    billingInfo: BillingInfo | null;
    companyEmail: string;
    ean: string;
    companyId: number;
    direction: "offtake" | "injection" | "combined";
    signerName: string;
    contractError?: string | null;
  } | null>(null);

  const [pvModalOpen, setPvModalOpen] = useState(false);
  const [pvContractId, setPvContractId] = useState<number | null>(null);
  const [pvPdfUrl, setPvPdfUrl] = useState<string | undefined>(undefined);
  const [pvHasCombinedServices, setPvHasCombinedServices] = useState(false);
  const [pvContractData, setPvContractData] = useState<PvCurtailmentContractData | null>(null);
  const [pvRegenerateLoading, setPvRegenerateLoading] = useState(false);
  const [pvSignBlockedReason, setPvSignBlockedReason] = useState<
    "INJECTION_UNSIGNED" | "OFFTAKE_UNSIGNED" | null
  >(null);
  const [sendProposalContractId, setSendProposalContractId] = useState<number | null>(null);
  const [bessSendProposalContractId, setBessSendProposalContractId] = useState<number | null>(null);
  const [commodityProposal, setCommodityProposal] = useState<{
    ean: string;
    direction: "injection" | "offtake" | "combined";
  } | null>(null);

  const [bessModalOpen, setBessModalOpen] = useState(false);
  const [bessContractId, setBessContractId] = useState<number | null>(null);
  const [bessPdfUrl, setBessPdfUrl] = useState<string | undefined>(undefined);
  const [bessContractData, setBessContractData] = useState<BessOnboardingContractData | null>(null);
  const [bessRegenerateLoading, setBessRegenerateLoading] = useState(false);
  const [bessSignBlockedReason, setBessSignBlockedReason] = useState<
    "INJECTION_UNSIGNED" | "OFFTAKE_UNSIGNED" | null
  >(null);
  const [bessHasCombinedServices, setBessHasCombinedServices] = useState(false);
  // Site-level certified controller — the backend blocks BESS signing (422) without one.
  const [bessHasCertifiedController, setBessHasCertifiedController] = useState(true);

  const bessResignPending = useSelector(selectPendingBessResigns);
  const bessResignPendingByInstallation = useMemo(() => {
    const map: Record<string, BessResignPendingRow> = {};
    for (const row of bessResignPending) map[row.installation_id] = row;
    return map;
  }, [bessResignPending]);

  const [bessResignLoadingContractId, setBessResignLoadingContractId] = useState<number | null>(null);
  const [bessResignActive, setBessResignActive] = useState<{
    row: BessResignPendingRow;
    pdfUrl: string;
    billingInfo: BessResignRegenerateBillingInfo;
    projectedStartDate: string;
  } | null>(null);

  const bessResignSignerName = `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim();

  const navigate = useNavigate();

  useEffect(() => {
    if (partnerStatus === "idle" && partners.length === 0 && accessToken) {
      dispatch(fetchPartners({ token: accessToken }) as unknown as PayloadAction);
    }
  }, [dispatch, partnerStatus, partners.length, accessToken]);

  const handleServiceDetails = (service: ServiceType) => {
    setSelectedService(service);
  };

  const servicesData = useMemo(() => {
    return data?.services || { injection: [], offtake: [], bess: [], eboiler: [], pvCurtailment: [] };
  }, [data?.services]);

  useEffect(() => {
    if (data?.services) {
      const allSiteKeys: Record<string, boolean> = {};

      const addSiteKey = (siteId: number | null, fallbackAddress: string) => {
        const key = siteId != null ? `site-${siteId}` : `addr-${fallbackAddress}`;
        allSiteKeys[key] = true;
      };

      servicesData.injection.forEach((s) => addSiteKey(s.site_id, getFullAddress(s)));
      servicesData.offtake.forEach((s) => addSiteKey(s.site_id, getFullAddress(s)));
      servicesData.bess.forEach((s) => addSiteKey(s.id, getFlexAddress(s)));
      (servicesData.eboiler || []).forEach((s) => addSiteKey(s.id, getFlexAddress(s)));
      servicesData.pvCurtailment.forEach((s) => addSiteKey(s.id, getFlexAddress(s)));

      setExpandedSites(allSiteKeys);
    }
  }, [data, servicesData]);

  useEffect(() => {
    const savedCompanyId = localStorage.getItem("addServiceCompanyId");
    if (
      savedCompanyId &&
      selectedUserCompany?.company_id &&
      savedCompanyId !== selectedUserCompany.company_id.toString()
    ) {
      localStorage.removeItem("addServiceFormData");
      localStorage.removeItem("formVolumes");
      localStorage.removeItem("selectedServices");
      localStorage.removeItem("contractOverview");
      localStorage.removeItem("pslData");
      localStorage.removeItem("pslFullData");
      localStorage.removeItem("requiresSeparateContracts");
      localStorage.setItem("addServiceCompanyId", selectedUserCompany.company_id.toString());
    } else {
      const formData = localStorage.getItem("addServiceFormData");
      if (formData) {
        try {
          JSON.parse(formData);
        } catch {
          // ignore
        }
      }
    }
  }, [selectedUserCompany?.company_id]);

  const serviceTypes = [
    { value: "injection", label: "Injection" },
    { value: "offtake", label: "Offtake" },
    { value: "bess", label: "BESS" },
    { value: "eboiler", label: "E-Boiler" },
    { value: "pvcurtailment", label: "PV Curtailment" },
  ];

  const contractStatuses = [
    { value: "active", label: "Active" },
    { value: "signed", label: "Signed" },
    { value: "unsigned", label: "Unsigned" },
    { value: "onboarding", label: "Onboarding" },
    { value: "not_generated", label: "Not generated" },
    { value: "future", label: "Future" },
    { value: "ended", label: "Ended" },
    { value: "expired", label: "Expired" },
  ];

  function getPayloadDirection(
    service: OfftakeService | InjectionService,
    direction: "offtake" | "injection" | "combined",
  ): "offtake" | "injection" | "combined" {
    if (service.service_component === "SC_OFFINJE") {
      return "combined";
    }
    return direction;
  }

  const handleShowUnsignedContract = async (
    service: OfftakeService | InjectionService,
    direction: "offtake" | "injection" | "combined",
  ) => {
    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      const payloadDirection = getPayloadDirection(service, direction);

      const payload = {
        ean: `${service.ean}`,
        name: `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "JOHN DOE",
        company_id: selectedUserCompany?.company_id,
        direction: payloadDirection,
      };

      const response = await toast.promise(
        axios.post(`${import.meta.env.VITE_API_SERVICES}/api/v1/contracts/generateContract/proposal`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
        {
          pending: {
            render: () => <PendingToast title="Generating" message="Generating contract proposal..." />,
          },
          success: {
            render: () => <SuccessToast title="Success" message="Contract generated!" />,
          },
          error: {
            render: ({ data }: { data: unknown }) => {
              // `generateContract/proposal` serialises coded refusals as `{ error, code }`
              // with no `message` alias, so reading `.message` found nothing and every
              // coded refusal surfaced as the generic string. Same shared resolver the
              // signing surfaces use: localised copy for a mapped code, otherwise the
              // backend's own sentence before the generic fallback.
              const { message, detail } = resolveContractErrorCopy(
                (data as { response?: { data?: ContractErrorBody } })?.response?.data,
                t,
                "Failed to load contract proposal.",
              );
              return <ErrorToast title="Error" message={detail ? `${message} ${detail}` : message} />;
            },
          },
        },
        {
          position: "bottom-right",
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          icon: false,
          transition: Bounce,
          style: {
            width: "400px",
            maxWidth: "800px",
          },
        },
      );

      let modalDirection: "offtake" | "injection" | "combined" = direction;
      if (response.data.offtake && response.data.injection) {
        modalDirection = "combined";
      } else if (response.data.offtake) {
        modalDirection = "offtake";
      } else if (response.data.injection) {
        modalDirection = "injection";
      }

      setSignatureModalData({
        pdfUrls: { offtake: response.data.offtake, injection: response.data.injection },
        billingInfo: response.data.billingInfo,
        companyEmail: response.data.companyEmail,
        ean: service.ean,
        companyId: selectedUserCompany?.company_id ?? 0,
        direction: modalDirection,
        signerName: selectedUserCompany?.companyName || "JOHN DOE",
        contractError: null,
      });
      setSignatureModalOpen(true);
    } catch (err: unknown) {
      // 🔴 THIS CATCH RUNS. `toast.promise` returns the ORIGINAL promise, not its own
      // `.catch` chain, so the rejection propagates to the `await` above — the resolved
      // toast and this block both happen.
      //
      // `contractError` is the surface that MATTERS here: the signature modal opens
      // below and stays open, while the toast auto-closes after 7s. So the durable
      // surface must carry the same real reason. Reading `.message` was the same dead
      // read the rest of this change removes — the contracts endpoints serialise
      // `{ error, code }` and publish no `message` alias — which left the modal showing
      // a generic string while the toast showed the truth.
      let errorMessage = "Failed to load contract proposal.";
      if (axios.isAxiosError(err)) {
        const { message, detail } = resolveContractErrorCopy(
          err.response?.data as ContractErrorBody | undefined,
          t,
          errorMessage,
        );
        errorMessage = detail ? `${message} ${detail}` : message;
      }
      setSignatureModalData({
        pdfUrls: { offtake: null, injection: null },
        billingInfo: null,
        companyEmail: "",
        ean: service.ean,
        companyId: selectedUserCompany?.company_id ?? 0,
        direction,
        signerName: selectedUserCompany?.companyName || "JOHN DOE",
        contractError: errorMessage,
      });
      setSignatureModalOpen(true);
      // No second toast. `toast.promise` has already rendered the RESOLVED reason, and
      // react-toastify STACKS rather than replaces — this hardcoded one put a generic
      // message on screen next to, and contradicting, the real one.
      Sentry.captureException(err);
    }
  };

  const handlePvCurtailmentRegenerate = async (service: FlexService) => {
    if (pvRegenerateLoading) return;
    setPvRegenerateLoading(true);
    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      if (!token) {
        toast(<ErrorToast title="Error" message="Authentication required." />, {
          ...toastConfig,
          icon: false,
        });
        return;
      }

      const installationId = service.sdp_flex_installation_id;
      if (!installationId) {
        toast(<ErrorToast title="Error" message="No installation ID found for this service." />, {
          ...toastConfig,
          icon: false,
        });
        return;
      }

      const todayStr = new Date().toISOString().split("T")[0];
      let adjustedStartDate: string | null = null;

      if (service.start_date && service.start_date < todayStr) {
        const hasBessOrEboiler =
          bessServices.some(
            (s) => s.site_unique_key === service.site_unique_key && s.contract_status === "active",
          ) ||
          eboilerServices.some(
            (s) => s.site_unique_key === service.site_unique_key && s.contract_status === "active",
          );

        adjustedStartDate = hasBessOrEboiler ? getFirstOfMonthOnOrAfter(todayStr) : todayStr;
      }

      const response = await toast.promise(
        axios.post(
          `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/site-steering/regenerate`,
          {
            installation_id: installationId,
            ...(adjustedStartDate ? { start_date: adjustedStartDate } : {}),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        ),
        {
          pending: {
            render: () => <PendingToast title="Regenerating" message="Regenerating contract..." />,
          },
          success: {
            render: () => <SuccessToast title="Success" message="Contract regenerated!" />,
          },
          error: {
            render: ({ data }: { data: unknown }) => {
              const axErr = data as { response?: { data?: { message?: string } } };
              return (
                <ErrorToast
                  title="Error"
                  message={axErr?.response?.data?.message || "Failed to regenerate contract."}
                />
              );
            },
          },
        },
        {
          position: "bottom-right",
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          icon: false,
          transition: Bounce,
          style: { width: "400px", maxWidth: "800px" },
        },
      );

      const resData = response.data;
      const resNested = resData.contract || {};
      const billingInfo = resData.billing_info || resNested.billing_info || {};

      setPvContractId(resData.id ?? resData.contract_id ?? service.contract_flex_id ?? null);
      setPvPdfUrl(resData.pdf_url);

      const reason = resData.signBlockedReason;
      setPvSignBlockedReason(
        reason === "INJECTION_UNSIGNED" || reason === "OFFTAKE_UNSIGNED" ? reason : null,
      );

      const formatDate = (d: string | null | undefined) => {
        if (!d) return "N/A";
        const parsed = new Date(d);
        return Number.isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString("en-GB");
      };

      setPvContractData({
        startDate: formatDate(resData.start_date || service.start_date),
        actualStart: resData.start_date || service.start_date || "",
        profitShare: (resData.profit_share ?? resNested.profit_share)
          ? `${parseFloat(resData.profit_share ?? resNested.profit_share)}%`
          : service.yuso_profit_share
            ? `${parseFloat(String(service.yuso_profit_share))}%`
            : "20%",
        monthlyFee: (resData.monthly_fee ?? resNested.monthly_fee)
          ? `${parseFloat(resData.monthly_fee ?? resNested.monthly_fee).toFixed(2)} EUR`
          : service.steering_fee_fixed
            ? `${parseFloat(String(service.steering_fee_fixed)).toFixed(2)} EUR`
            : "-",
        partner: resData.partner_name || resNested.partner_name || partners.find((p) => p.id === service.partner_id)?.name || (service.partner_id ? `Partner #${service.partner_id}` : "-"),
        peakPower: (resData.nominal_power ?? resNested.nominal_power) ? `${resData.nominal_power ?? resNested.nominal_power} kWp` : "-",
        inverterPower: (resData.inverter_power ?? resNested.inverter_power) ? `${resData.inverter_power ?? resNested.inverter_power} kVA` : "-",
        controller: resData.controller || resNested.controller || String(service.yuso_certified_controller || "-"),
        opportunityCost: (resData.opportunity_cost ?? resNested.opportunity_cost) != null ? `${resData.opportunity_cost ?? resNested.opportunity_cost} €/MWh` : "-",
        validUntil: formatDate(resData.end_date || service.end_date) === "N/A"
          ? "Auto"
          : formatDate(resData.end_date || service.end_date),
        company: {
          name: billingInfo.company_name || selectedUserCompany?.companyName || "-",
          address: billingInfo.address
            ? `${billingInfo.address}\n${billingInfo.zip || ""} ${billingInfo.city || ""}`.trim()
            : "-",
          vat: billingInfo.vat_code || selectedUserCompany?.vatCode || "-",
          email: billingInfo.email || "-",
          iban: billingInfo.iban || "-",
        },
        installationId: resData.installation_id || installationId,
        companyId: resData.company_id || service.company_id || selectedUserCompany?.company_id || 0,
        siteName: resData.site_name || service.name || "",
        endDate: resData.end_date || service.end_date || null,
        partnerId: resData.partner_id || service.partner_id || 0,
        profitShareValue: resData.profit_share || resNested.profit_share || String(service.yuso_profit_share || "20.00000"),
        monthlyFeeValue: resData.monthly_fee || resNested.monthly_fee || String(service.steering_fee_fixed || "0.00000"),
        steeringFeePerformancePct: resData.steering_fee_performance_pct || resNested.steering_fee_performance_pct || "20.00000",
        inverterPowerKva: resData.inverter_power ?? resNested.inverter_power ?? 0,
        opportunityCostValue: resData.opportunity_cost ?? resNested.opportunity_cost ?? 0,
        injectionInstallationId: resData.injection_installation_id || service.injection_installation_id || "",
        offtakeInstallationId: resData.offtake_installation_id || service.offtake_installation_id || null,
      });

      const injId = service.injection_installation_id;
      const hasCombined =
        bessServices.some((b) => b.injection_installation_id === injId) ||
        eboilerServices.some((e) => e.injection_installation_id === injId);
      setPvHasCombinedServices(hasCombined);
      setPvModalOpen(true);
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setPvRegenerateLoading(false);
    }
  };

  const handleBessResignClick = async (row: BessResignPendingRow) => {
    const session = getSessionFromCookie();
    const token = session?.getAccessToken().getJwtToken();
    if (!token) {
      toast(<ErrorToast title="Error" message="You're signed out. Please log in and try again." />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });
      return;
    }
    setBessResignLoadingContractId(row.contract_id);
    try {
      const { pdf_url, billing_info, projected_start_date } = await regenerateBessResignPdf(row.contract_id, token);
      setBessResignActive({
        row,
        pdfUrl: pdf_url,
        billingInfo: billing_info,
        projectedStartDate: projected_start_date,
      });
    } catch (err) {
      const status = err instanceof BessResignError ? err.status : 0;
      const message =
        status === 401
          ? "You're signed out. Please log in and try again."
          : status === 403
            ? "You don't have permission to re-sign this contract."
            : status === 404
              ? "This contract is no longer available. Please refresh."
              : status === 409
                ? "This contract is no longer pending. Please refresh."
                : status === 410
                  ? "This contract is already signed."
                  : "We couldn't generate the contract right now. Please try again.";
      toast(<ErrorToast title="Error" message={message} />, { ...toastConfig, autoClose: 5000, icon: false });
    } finally {
      setBessResignLoadingContractId(null);
    }
  };

  const handleBessResignSuccess = () => {
    setBessResignActive(null);
    const session = getSessionFromCookie();
    const token = session?.getAccessToken().getJwtToken();
    if (!token || !selectedUserCompany?.company_id) return;
    dispatch(fetchPendingBessResignsThunk({ companyId: selectedUserCompany.company_id, token }) as never);
    if (selectedUserCompany.companyNumber) {
      dispatch(fetchPortfolioServices({ companyNumber: selectedUserCompany.companyNumber, token }) as never);
    }
  };

  const handlePvCurtailmentSign = async (signatureData: ContractSigningPayload) => {
    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      if (!token) {
        throw new Error("Authentication required");
      }

      const payload = {
        ...signatureData,
        contract_id: pvContractId,
      };

      await axios.post(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/site-steering/sign`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast(<SuccessToast title="Success" message="PV Curtailment contract signed successfully!" />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });

      setPvModalOpen(false);

      if (selectedUserCompany?.companyNumber && token) {
        dispatch(fetchPortfolioServices({ companyNumber: selectedUserCompany.companyNumber, token }) as never);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to sign contract";
      toast(<ErrorToast title="Error" message={errorMessage} />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });
      Sentry.captureException(err);
      throw err;
    }
  };

  const handleBessRegenerate = async (service: FlexService) => {
    if (bessRegenerateLoading) return;
    setBessRegenerateLoading(true);
    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      if (!token) {
        toast(<ErrorToast title="Error" message="Authentication required." />, {
          ...toastConfig,
          icon: false,
        });
        return;
      }

      const installationId = service.sdp_flex_installation_id;
      if (!installationId) {
        toast(<ErrorToast title="Error" message="No installation ID found for this service." />, {
          ...toastConfig,
          icon: false,
        });
        return;
      }

      const todayStr = new Date().toISOString().split("T")[0];
      let adjustedStartDate: string | null = null;
      if (service.start_date && service.start_date < todayStr) {
        adjustedStartDate = getFirstOfMonthOnOrAfter(todayStr);
      }

      const response = await toast.promise(
        axios.post(
          `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/bess/regenerate`,
          {
            installation_id: installationId,
            ...(adjustedStartDate ? { start_date: adjustedStartDate } : {}),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        ),
        {
          pending: {
            render: () => <PendingToast title="Regenerating" message="Regenerating contract..." />,
          },
          success: {
            render: () => <SuccessToast title="Success" message="Contract regenerated!" />,
          },
          error: {
            render: ({ data }: { data: unknown }) => {
              const axErr = data as { response?: { data?: { message?: string } } };
              return (
                <ErrorToast
                  title="Error"
                  message={axErr?.response?.data?.message || "Failed to regenerate contract."}
                />
              );
            },
          },
        },
        {
          position: "bottom-right",
          autoClose: 7000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          icon: false,
          transition: Bounce,
          style: { width: "400px", maxWidth: "800px" },
        },
      );

      const resData = response.data;
      const resNested = resData.contract || {};
      const billingInfo = resData.billing_info || resNested.billing_info || {};

      setBessContractId(resData.id ?? resData.contract_id ?? service.contract_flex_id ?? null);
      setBessPdfUrl(resData.pdf_url);

      const reason = resData.signBlockedReason;
      setBessSignBlockedReason(
        reason === "INJECTION_UNSIGNED" || reason === "OFFTAKE_UNSIGNED" ? reason : null,
      );

      const formatDate = (d: string | null | undefined) => {
        if (!d) return "N/A";
        const parsed = new Date(d);
        return Number.isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString("en-GB");
      };

      setBessContractData({
        startDate: formatDate(resData.start_date || service.start_date),
        actualStart: resData.start_date || service.start_date || "",
        profitShare: (resData.profit_share ?? resNested.profit_share)
          ? `${parseFloat(resData.profit_share ?? resNested.profit_share)}%`
          : service.yuso_profit_share
            ? `${parseFloat(String(service.yuso_profit_share))}%`
            : "10%",
        monthlyFee: (resData.monthly_fee ?? resNested.monthly_fee)
          ? `${parseFloat(resData.monthly_fee ?? resNested.monthly_fee).toFixed(2)} EUR`
          : service.steering_fee_fixed
            ? `${parseFloat(String(service.steering_fee_fixed)).toFixed(2)} EUR`
            : "-",
        partner: resData.partner_name || resNested.partner_name || partners.find((p) => p.id === service.partner_id)?.name || (service.partner_id ? `Partner #${service.partner_id}` : "-"),
        peakPower: (resData.nominal_power ?? resNested.nominal_power) ? `${resData.nominal_power ?? resNested.nominal_power} kW` : "-",
        inverterPower: (resData.nominal_power ?? resNested.nominal_power) ? `${resData.nominal_power ?? resNested.nominal_power} kW` : "-",
        controller: resData.controller || resNested.controller || String(service.yuso_certified_controller || "-"),
        opportunityCost: "-",
        validUntil: formatDate(resData.end_date || service.end_date) === "N/A"
          ? "Auto"
          : formatDate(resData.end_date || service.end_date),
        company: {
          name: billingInfo.company_name || selectedUserCompany?.companyName || "-",
          address: billingInfo.address
            ? `${billingInfo.address}\n${billingInfo.zip || ""} ${billingInfo.city || ""}`.trim()
            : "-",
          vat: billingInfo.vat_code || selectedUserCompany?.vatCode || "-",
          email: billingInfo.email || "-",
          iban: billingInfo.iban || "-",
        },
        installationId: resData.installation_id || installationId,
        companyId: resData.company_id || service.company_id || selectedUserCompany?.company_id || 0,
        siteName: resData.site_name || service.name || "",
        endDate: resData.end_date || service.end_date || null,
        partnerId: resData.partner_id || service.partner_id || 0,
        profitShareValue: resData.profit_share || resNested.profit_share || String(service.yuso_profit_share || "10.00000"),
        monthlyFeeValue: resData.monthly_fee || resNested.monthly_fee || String(service.steering_fee_fixed || "0.00000"),
        steeringFeePerformancePct: resData.steering_fee_performance_pct || resNested.steering_fee_performance_pct || "20.00000",
        inverterPowerKva: resData.nominal_power ?? resNested.nominal_power ?? 0,
        opportunityCostValue: 0,
        injectionInstallationId: resData.injection_installation_id || service.injection_installation_id || "",
        offtakeInstallationId: resData.offtake_installation_id || service.offtake_installation_id || null,
      });

      const injId = service.injection_installation_id;
      const hasCombined =
        eboilerServices.some((e) => e.injection_installation_id === injId && e.contract_status === "active");
      setBessHasCombinedServices(hasCombined);
      setBessHasCertifiedController(service.yuso_certified_controller != null);
      setBessModalOpen(true);
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setBessRegenerateLoading(false);
    }
  };

  const handleBessSign = async (signatureData: ContractSigningPayload) => {
    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      if (!token) {
        throw new Error("Authentication required");
      }

      const payload = {
        ...signatureData,
        contract_id: bessContractId,
      };

      await axios.post(
        `${import.meta.env.VITE_API_SERVICES}/api/v1/sdp/bess/sign`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast(<SuccessToast title="Success" message="BESS contract signed successfully!" />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });

      setBessModalOpen(false);

      if (selectedUserCompany?.companyNumber && token) {
        dispatch(fetchPortfolioServices({ companyNumber: selectedUserCompany.companyNumber, token }) as never);
      }
    } catch (err) {
      const controllerRequired =
        axios.isAxiosError(err) &&
        err.response?.status === 422 &&
        (err.response?.data as { error?: string })?.error === "CONTROLLER_REQUIRED";
      const errorMessage = controllerRequired
        ? t("controllerRequired.toast", { ns: "bessOnboarding" })
        : axios.isAxiosError(err)
          ? err.response?.data?.message ?? err.message
          : err instanceof Error
            ? err.message
            : "Failed to sign contract";
      toast(<ErrorToast title="Error" message={errorMessage} />, {
        ...toastConfig,
        autoClose: 5000,
        icon: false,
      });
      Sentry.captureException(err);
      throw err;
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const groupedServices = useMemo(() => {
    const siteGroups: Record<string, SiteGroup> = {};

    function getSiteKey(siteId: number | null): string {
      if (siteId != null) return `site-${siteId}`;
      return "ungrouped";
    }

    function ensureGroup(key: string, displayName: string) {
      if (!siteGroups[key]) {
        siteGroups[key] = { siteKey: key, siteName: displayName, services: [] };
      }
    }

    function updateGroupNameIfBetter(key: string, siteName: string | null) {
      if (siteName && siteGroups[key]) {
        siteGroups[key].siteName = siteName;
      }
    }

    const shouldIncludeService = (service: ServiceType, type: string) => {
      const searchableFields: string[] = [];

      if (isFlexService(service)) {
        searchableFields.push(
          service.address_street || "",
          service.address_number || "",
          service.address_zip || "",
          service.address_city || "",
          service.name || "",
          service.sdp_flex_installation_id || "",
          service.product_name || "",
        );
      } else {
        searchableFields.push(
          service.addressStreet || "",
          service.addressNumber || "",
          String(service.addressZip || ""),
          service.addressCity || "",
          service.ean || "",
          service.site_name || "",
        );
      }

      const matchesSearch = !searchTerm || searchableFields.some((field) => field.toLowerCase().includes(searchTerm));
      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(type.toLowerCase());

      let matchesStatus = true;
      if (selectedStatuses.length > 0) {
        let status: string | null = null;
        if (isInjectionService(service) || isOfftakeService(service)) {
          status = service.status;
        } else if (isFlexService(service)) {
          status = service.contract_status || getFlexContractStatus(service);
        }
        matchesStatus = status ? selectedStatuses.includes(status.toLowerCase()) : false;
      }

      return matchesSearch && matchesType && matchesStatus;
    };

    servicesData.injection.forEach((service) => {
      if (!shouldIncludeService(service, "injection")) return;
      const address = getFullAddress(service);
      const key = getSiteKey(service.site_id);
      ensureGroup(key, key === "ungrouped" ? "Other" : (service.site_name || address));
      updateGroupNameIfBetter(key, service.site_name);
      siteGroups[key].services.push({ type: "injection", data: service });
    });

    servicesData.offtake.forEach((service) => {
      if (!shouldIncludeService(service, "offtake")) return;
      const address = getFullAddress(service);
      const key = getSiteKey(service.site_id);
      ensureGroup(key, key === "ungrouped" ? "Other" : (service.site_name || address));
      updateGroupNameIfBetter(key, service.site_name);
      siteGroups[key].services.push({ type: "offtake", data: service });
    });

    servicesData.bess.forEach((service) => {
      if (!shouldIncludeService(service, "bess")) return;
      const address = getFlexAddress(service);
      const key = getSiteKey(service.id);
      ensureGroup(key, service.name || address);
      updateGroupNameIfBetter(key, service.name);
      siteGroups[key].services.push({ type: "BESS", data: service });
    });

    (servicesData.eboiler || []).forEach((service) => {
      if (!shouldIncludeService(service, "eboiler")) return;
      const address = getFlexAddress(service);
      const key = getSiteKey(service.id);
      ensureGroup(key, service.name || address);
      updateGroupNameIfBetter(key, service.name);
      siteGroups[key].services.push({ type: "E-Boiler", data: service });
    });

    servicesData.pvCurtailment.forEach((service) => {
      if (!shouldIncludeService(service, "pvcurtailment")) return;

      const status = service.contract_status || getFlexContractStatus(service);
      if (status === "not_generated" && !findInjectionAtAddress(service, servicesData.injection)) return;

      const address = getFlexAddress(service);
      const key = getSiteKey(service.id);
      ensureGroup(key, service.name || address);
      updateGroupNameIfBetter(key, service.name);
      siteGroups[key].services.push({ type: "PV Curtailment", data: service });
    });

    return Object.values(siteGroups);
  }, [servicesData, searchTerm, selectedTypes, selectedStatuses]);

  const toggleSite = (siteKey: string) => {
    setExpandedSites((prev) => ({
      ...prev,
      [siteKey]: !prev[siteKey],
    }));
  };

  const hasServices = groupedServices.length > 0;


  const renderPhysicalStatus = (service: ServiceType) => {
    const physicalStatus =
      isInjectionService(service) || isOfftakeService(service) ? service.physicalStatus : undefined;

    if (physicalStatus && ["Disconnected", "Under construction", "Demolished"].includes(physicalStatus)) {
      return (
        <div className="relative group">
          <div className="bg-[#FFD9D9] p-1.5 rounded-full cursor-help">
            <WarningIcon width={14} height={14} />
          </div>
          <div className="absolute invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            {physicalStatus}
          </div>
        </div>
      );
    }
    return null;
  };

  const getServiceIdentifier = (service: ServiceType) => {
    if (isFlexService(service)) {
      return service.name || service.sdp_flex_installation_id || "-";
    }
    return service.ean;
  };

  const formatDDMMYYYY = (d: string | null | undefined): string => {
    if (!d) return "N/A";
    const parsed = new Date(d);
    if (Number.isNaN(parsed.getTime())) return d;
    return parsed.toLocaleDateString("en-GB");
  };

  const isValidDateStr = (d: string | null | undefined): boolean => {
    if (!d || d === "0000-00-00") return false;
    const parsed = new Date(d);
    return !Number.isNaN(parsed.getTime());
  };

  const getServiceStatus = (service: ServiceType) => {
    if (isInjectionService(service) || isOfftakeService(service)) {
      return service.status;
    }

    if (isFlexService(service)) {
      if (service.contract_status) {
        return service.contract_status;
      }
      return getFlexContractStatus(service);
    }

    return null;
  };


  // FT-1189: the installation id the status endpoint keys this row by.
  const getStatusKey = (service: ServiceType): string | null => {
    if (isFlexService(service)) {
      return service.sdp_flex_installation_id ?? null;
    }
    if (isInjectionService(service) || isOfftakeService(service)) {
      return service.installation_id ?? null;
    }
    return null;
  };

  const getRedirectUrl = insideServiceUrl;

  const getServiceEndDate = (service: ServiceType) => {
    if (isFlexService(service)) {
      return isValidDateStr(service.end_date) ? service.end_date : null;
    }
    if ((isInjectionService(service) || isOfftakeService(service)) && isValidDateStr(service.endDate)) {
      return service.endDate;
    }
    return null;
  };

  const renderEndDate = (service: ServiceType) => {
    const displayed = getDisplayedContract(service);
    if (displayed) {
      return displayed.end_date ? formatDDMMYYYY(displayed.end_date) : "";
    }

    const endDate = getServiceEndDate(service);
    const status = getServiceStatus(service);

    if (status?.toLowerCase() === "active" && !endDate) {
      return <span className="text-primary-500 italic"></span>;
    }

    if (endDate) {
      return formatDDMMYYYY(endDate);
    }

    return "";
  };

  /** Pure string-split DD/MM/YYYY (no Date parsing) for the confirmed EOC date. */
  const formatEocDate = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (!y || !m || !d) return iso;
    return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
  };

  /**
   * Passive confirmed-EOC badge (FT-4389 T4). Reads eocStatus/eocDate off the
   * injection/offtake `/allservices` row: approved → green "Accepted · ends
   * <date>" (prefers eocDate over the contract endDate); rejected → red
   * "Rejected". Read-only — never alters signing-flow behavior. Flex rows have
   * no eoc* fields, so nothing renders for them.
   *
   * `eocStatus` mirrors the raw Atrias StatusType code ('39' approved / '41'
   * rejected); the label vocabulary ('approved'/'rejected') lives on
   * `mig6_eoc_requests.status`. Both are accepted, and unknown codes render
   * nothing — that whole decision lives in the SHARED module
   * (src/utils/eocStatus), which EndContractManager reads too, so the two
   * surfaces can never disagree.
   *
   * Read through `eocBadgeOutcome`, NOT the bare normalizer: an `eocStatus` of
   * '39' with `eocStatusReason = 'COMPLETED'` was stamped by a CUSTOMER SWITCH
   * (the SDP was handed to another supplier), not by an end-of-contract the
   * customer requested, so no EOC outcome wording may be shown for it.
   */
  const renderEocOutcome = (service: ServiceType): React.ReactNode => {
    if (!isInjectionService(service) && !isOfftakeService(service)) return null;
    const status = eocBadgeOutcome(service.eocStatus, service.eocStatusReason);
    if (status === "approved") {
      const endsOn = service.eocDate
        ? formatEocDate(service.eocDate)
        : isValidDateStr(service.endDate)
          ? formatDDMMYYYY(service.endDate)
          : "";
      return (
        <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
          {endsOn
            ? t("endContractEocApprovedEnds", { date: endsOn })
            : t("endContractEocApprovedLabel")}
        </span>
      );
    }
    if (status === "rejected") {
      return (
        <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
          {t("endContractEocRejectedLabel")}
        </span>
      );
    }
    return null;
  };

  /** The contract the status chip actually describes (backend picks the most
   *  relevant one) — dates in the row must come from the SAME contract, or an
   *  Active chip shows the successor's future start date (Alex, 2026-07-16). */
  const getDisplayedContract = (service: ServiceType) => {
    const key = getStatusKey(service);
    const entry = key ? sdpStatuses.get(key) : undefined;
    if (!entry?.display_status) return undefined;
    return entry.contracts.find((c) => c.status === entry.display_status);
  };

  const renderStartDate = (service: ServiceType) => {
    const displayed = getDisplayedContract(service);
    if (displayed?.start_date) {
      const startStr = formatDDMMYYYY(displayed.start_date);
      if (displayed.commencement_date) {
        return (
          <div>
            <div>{startStr}</div>
            <div className="text-xs text-primary-300">Comm. {formatDDMMYYYY(displayed.commencement_date)}</div>
          </div>
        );
      }
      return startStr;
    }

    if (isFlexService(service)) {
      const dateToShow = service.start_date || service.signature_date;
      const startStr = dateToShow ? formatDDMMYYYY(dateToShow) : "N/A";

      if (service.commencement_date) {
        return (
          <div>
            <div>{startStr}</div>
            <div className="text-xs text-primary-300">Comm. {formatDDMMYYYY(service.commencement_date)}</div>
          </div>
        );
      }

      return startStr;
    }

    return "startDate" in service && service.startDate ? formatDDMMYYYY(service.startDate) : "N/A";
  };

  function hasInstallationId(obj: unknown): obj is { installation_id: string } {
    return (
      typeof obj === "object" &&
      obj !== null &&
      "installation_id" in obj &&
      typeof (obj as { installation_id: unknown }).installation_id === "string"
    );
  }

  const getFlexInstallationId = (service: ServiceType): string | null => {
    if (isFlexService(service)) {
      return service.sdp_flex_installation_id || null;
    }
    return null;
  };

  return (
    <div className="md:mx-16 pb-[80px] mt-20 ">
      <ToastContainer {...toastConfig} />
      {(pvRegenerateLoading || bessRegenerateLoading || bessResignLoadingContractId !== null) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/60 dark:bg-primary-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-primary-900 rounded-2xl shadow-2xl border border-primary-100 dark:border-primary-800 px-12 py-9 flex flex-col items-center gap-5">
            <div className="flex items-end gap-[4px] h-9">
              {([0, 0.1, 0.22, 0.12, 0.05] as number[]).map((delay, i) => (
                <div
                  key={i}
                  className="w-[5px] bg-primary-600 rounded-full"
                  style={{ animation: `eq-bar 1.1s ease-in-out infinite`, animationDelay: `${delay}s` }}
                />
              ))}
            </div>
            <div className="text-center">
              <p className="text-primary-700 dark:text-primary-100 font-semibold text-[15px] tracking-tight">
                Preparing your contract
              </p>
              <p className="text-primary-400 dark:text-primary-400 text-sm mt-1">
                Generating PDF · this may take a moment
              </p>
            </div>
          </div>
        </div>
      )}
      <BessResignBanner />
      <h1 className="text-primary-600 dark:text-white  mb-14 lg:text-[1.875rem] font-semibold">Portfolio</h1>
      <div className=" border-r-[1px] border-l-[1px] border-b-[1px] dark:border-primary-800 rounded-lg">
        <div className="relative bg-white dark:bg-primary-900 z-[40]  border-t-[1px] border-gray-200 rounded-lg">
          <div className="flex items-center gap-3 p-3">
            <SearchInput onChange={handleSearchChange} placeholder="Search " className="" />
            <div className="relative">
              <MultiSelectDropdown
                options={serviceTypes}
                selectedValues={selectedTypes}
                onChange={setSelectedTypes}
                placeholder="Select service type"
                className=""
                styles="relative z-[100]"
              />
            </div>
            <div className="relative">
              <MultiSelectDropdown
                options={contractStatuses}
                selectedValues={selectedStatuses}
                onChange={setSelectedStatuses}
                placeholder="Select contract status"
                className=""
                styles="relative z-[100]"
              />
            </div>
          </div>
        </div>
        <div
          className="overflow-x-auto  max-h-[500px] md:max-h-[65vh] lg:max-h-[65vh] bg-white dark:bg-primary-900  text-sm rounded-lg"
          style={{ overflowY: "scroll" }}
        >
          <table className="table w-full">
            <thead className="bg-gray-50 dark:bg-primary-950 border-t-[1px] border-gray-200 dark:border-primary-800 z-40">
              <tr className="sticky top-0 child:py-5 text-primary-600 dark:text-white font-semibold text-sm z-30">
                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-t-[1px] border-gray-200 dark:border-primary-800 py-5 text-primary-600 dark:text-white font-semibold">
                  Service
                </th>

                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-t-[1px] border-gray-200 dark:border-primary-800 py-5 text-primary-600 dark:text-white font-semibold">
                  Contract status
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-t-[1px] border-gray-200 dark:border-primary-800 py-5 text-primary-600 dark:text-white font-semibold">
                  Start date
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-t-[1px] border-gray-200 dark:border-primary-800 py-5 text-primary-600 dark:text-white font-semibold">
                  End date
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-t-[1px] border-gray-200 dark:border-primary-800 py-5 text-primary-600 dark:text-white font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                    <td colSpan={6} className="text-center">
                    <Spinner />
                  </td>
                </tr>
              ) : !hasServices ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No results found
                  </td>
                </tr>
              ) : (
                groupedServices.map((group) => (
                  <React.Fragment key={group.siteKey}>
                    <tr
                      className=" dark:bg-primary-800 font-semibold text-sm text-primary-600 cursor-pointer hover:bg-gray-200 dark:hover:bg-primary-700"
                      onClick={() => toggleSite(group.siteKey)}
                    >
                      <td colSpan={6} className="font-bold py-5">
                        <div className="flex items-center">
                          <span
                            className="mr-2 transform transition-transform duration-200"
                            style={{
                              display: "inline-block",
                              transform: expandedSites[group.siteKey] ? "rotate(90deg)" : "rotate(0deg)",
                            }}
                          >
                            ▶
                          </span>
                          {group.siteName}
                        </div>
                      </td>
                    </tr>

                    {expandedSites[group.siteKey] &&
                      group.services.map((service, serviceIndex) => {
                        const isFlexType =
                          service.type === "BESS" || service.type === "E-Boiler" || service.type === "PV Curtailment";

                        const flexInstallationId = isFlexService(service.data)
                          ? service.data.sdp_flex_installation_id
                          : null;
                        const pendingResign =
                          (service.type === "BESS" || service.type === "PV Curtailment") && flexInstallationId
                            ? bessResignPendingByInstallation[flexInstallationId]
                            : undefined;

                        return (
                          <tr
                            className="child:text-sm bg-white dark:bg-primary-900 hover:bg-gray-50 dark:hover:bg-primary-800"
                            key={`${group.siteKey}-${service.type}-${serviceIndex}`}
                          >
                            <td className="text-primary-600 dark:text-white pl-10 flex items-center gap-2">
                              <ReturnArrowIcon
                                width={16}
                                height={16}
                                stroke="currentColor"
                                className="text-primary-400 dark:text-primary-300"
                              />
                              <span>
                                {getServiceIdentifier(service.data)} -{" "}
                                {isFlexService(service.data) && service.data.asset_type
                                  ? service.data.asset_type
                                  : service.type.charAt(0).toUpperCase() + service.type.slice(1)}
                              </span>
                              {renderPhysicalStatus(service.data)}
                            </td>

                            <td className="text-primary-400 dark:text-primary-100">
                              {/* A pending re-sign is a FLAG, not a status: the service is still
                                  running while it waits to be re-signed. Showing the flag alone
                                  (as this did) read as though the contract had stopped being
                                  active. Both are rendered now — the flag keeps its place, with
                                  the real contract status beside it (Alex, 2026-09-16). */}
                              <div className="flex flex-wrap items-center gap-1.5">
                                {pendingResign && (
                                  <span className="px-2 py-1 rounded-md inline-block bg-[#FFEDD5] text-[#9A3412] border border-[#FED7AA] font-medium">
                                    Needs re-signing
                                  </span>
                                )}
                                <ContractStatusChip
                                  entry={
                                    getStatusKey(service.data)
                                      ? sdpStatuses.get(getStatusKey(service.data)!)
                                      : undefined
                                  }
                                  fallbackStatus={getServiceStatus(service.data)}
                                />
                              </div>
                            </td>
                            <td className="text-primary-400 dark:text-primary-100">{renderStartDate(service.data)}</td>
                            <td className="text-primary-400 dark:text-primary-100">
                              <div className="flex flex-col items-start gap-1">
                                <span>{renderEndDate(service.data)}</span>
                                {renderEocOutcome(service.data)}
                              </div>
                            </td>
                            <td className="text-primary-600 dark:text-primary-50 font-semibold flex items-center justify-end gap-3 pr-8">
                              {pendingResign && (
                                <button
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-[#9A3412] bg-[#FFEDD5] border border-[#FED7AA] hover:bg-[#FED7AA] hover:border-[#FB923C] transition-all duration-150 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Review and re-sign updated contract"
                                  disabled={bessResignLoadingContractId !== null}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBessResignClick(pendingResign);
                                  }}
                                >
                                  <SignatureIcon />
                                  Review & sign
                                </button>
                              )}
                              {/* Sign button for unsigned injection/offtake */}
                              {(isInjectionService(service.data) || isOfftakeService(service.data)) &&
                                needsCommoditySignature(
                                  getStatusKey(service.data)
                                    ? sdpStatuses.get(getStatusKey(service.data)!)
                                    : undefined,
                                  getServiceStatus(service.data)
                                ) && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap"
                                    title="Show unsigned contract"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (isInjectionService(service.data) || isOfftakeService(service.data)) {
                                        await handleShowUnsignedContract(
                                          service.data,
                                          service.type as "offtake" | "injection" | "combined",
                                        );
                                      }
                                    }}
                                  >
                                    <SignatureIcon />
                                    Sign Contract
                                  </button>
                                )}
                              {/* Send proposal button for unsigned injection/offtake — admin & partner */}
                              {(isInjectionService(service.data) || isOfftakeService(service.data)) &&
                                needsCommoditySignature(
                                  getStatusKey(service.data)
                                    ? sdpStatuses.get(getStatusKey(service.data)!)
                                    : undefined,
                                  getServiceStatus(service.data)
                                ) &&
                                canSendProposal && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap"
                                    title={tActiveServices("sendProposal.title")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const svc = service.data as OfftakeService | InjectionService;
                                      setCommodityProposal({
                                        ean: String(svc.ean),
                                        direction: getPayloadDirection(
                                          svc,
                                          service.type as "offtake" | "injection" | "combined",
                                        ),
                                      });
                                    }}
                                  >
                                    <SendIcon />
                                    {tActiveServices("sendProposal.sendProposalButton")}
                                  </button>
                                )}
                              {/* Sign button for unsigned PV Curtailment */}
                              {isFlexService(service.data) &&
                                service.type === "PV Curtailment" &&
                                getServiceStatus(service.data) === "unsigned" && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Sign PV curtailment contract"
                                    disabled={pvRegenerateLoading}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await handlePvCurtailmentRegenerate(service.data as FlexService);
                                    }}
                                  >
                                    <SignatureIcon />
                                    Sign Contract
                                  </button>
                                )}
                              {/* Send proposal button for unsigned PV Curtailment — admin & partner */}
                              {isFlexService(service.data) &&
                                service.type === "PV Curtailment" &&
                                getServiceStatus(service.data) === "unsigned" &&
                                canSendProposal && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                    title={tActiveServices("sendProposal.title")}
                                    disabled={pvRegenerateLoading}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const flexService = service.data as FlexService;
                                      if (flexService.contract_flex_id != null) {
                                        setSendProposalContractId(flexService.contract_flex_id);
                                      } else {
                                        toast(<ErrorToast title="Error" message="No contract found for this service." />, {
                                          ...toastConfig,
                                          autoClose: 5000,
                                          icon: false,
                                        });
                                      }
                                    }}
                                  >
                                    <SendIcon />
                                    {tActiveServices("sendProposal.sendProposalButton")}
                                  </button>
                                )}
                              {/* Complete setup button for not_generated PV Curtailment with injection at same site */}
                              {isFlexService(service.data) &&
                                service.type === "PV Curtailment" &&
                                (service.data.contract_status || getFlexContractStatus(service.data as FlexService)) === "not_generated" && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap"
                                    title="Complete PV curtailment setup"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const matchedInj = findInjectionAtAddress(
                                        service.data as FlexService,
                                        servicesData.injection
                                      );
                                      if (matchedInj) {
                                        dispatch(resetStep1());
                                        // resetStep1 wipes the form data but the wizard position lives in a
                                        // separate key — without clearing it the wizard restores straight to
                                        // step 4 with empty data (#466)
                                        localStorage.removeItem(PV_STORAGE_KEYS.wizard);
                                        dispatch(setInjectionSelection({ ean: matchedInj.ean, injection: null }));
                                        navigate("/pv-curtailment");
                                      }
                                    }}
                                  >
                                    <ArrowLongRightIcon />
                                    Complete setup
                                  </button>
                                )}
                              {/* Sign button for unsigned BESS */}
                              {isFlexService(service.data) &&
                                service.type === "BESS" &&
                                getServiceStatus(service.data) === "unsigned" && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                    title="Sign BESS contract"
                                    disabled={bessRegenerateLoading}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      await handleBessRegenerate(service.data as FlexService);
                                    }}
                                  >
                                    <SignatureIcon />
                                    Sign Contract
                                  </button>
                                )}
                              {/* Sign button for unsigned E-Boiler (stub) */}
                              {isFlexService(service.data) &&
                                service.type === "E-Boiler" &&
                                getServiceStatus(service.data) === "unsigned" && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap"
                                    title="Show unsigned contract"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toast.info(`${service.type} contract signing coming soon!`);
                                    }}
                                  >
                                    <SignatureIcon />
                                    Sign Contract
                                  </button>
                                )}
                              {/* Send proposal button for unsigned BESS — admin & partner */}
                              {isFlexService(service.data) &&
                                service.type === "BESS" &&
                                getServiceStatus(service.data) === "unsigned" &&
                                canSendProposal && (
                                  <button
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-200 hover:bg-primary-100 hover:border-primary-400 transition-all duration-150 whitespace-nowrap"
                                    title={tActiveServices("sendProposal.title")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const flexService = service.data as FlexService;
                                      if (flexService.contract_flex_id != null) {
                                        setBessSendProposalContractId(flexService.contract_flex_id);
                                      } else {
                                        toast(<ErrorToast title="Error" message="No contract found for this service." />, {
                                          ...toastConfig,
                                          autoClose: 5000,
                                          icon: false,
                                        });
                                      }
                                    }}
                                  >
                                    <SendIcon />
                                    {tActiveServices("sendProposal.sendProposalButton")}
                                  </button>
                                )}
                              <button
                                className="text-blue-500 hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleServiceDetails(service.data);
                                }}
                              >
                                <SearchIcon />
                              </button>
                              {isFlexType
                                ? getFlexInstallationId(service.data) &&
                                  getServiceStatus(service.data) === "active" && (
                                    <a
                                      href={getRedirectUrl({
                                        service_type: service.type,
                                        installation_id: getFlexInstallationId(service.data)!,
                                      })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-500 hover:underline"
                                    >
                                      <DataIcon />
                                    </a>
                                  )
                                : hasInstallationId(service.data) &&
                                  ["active", "ended"].includes(getServiceStatus(service.data) ?? "") && (
                                    <a
                                      href={getRedirectUrl({
                                        service_type: service.type,
                                        installation_id: (service.data as InjectionService | OfftakeService).installation_id,
                                      })}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-500 hover:underline"
                                    >
                                      <DataIcon />
                                    </a>
                                  )}
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
          {signatureModalOpen && signatureModalData && (
            <DigitalSignatureModal
              isOpen={signatureModalOpen}
              onClose={() => setSignatureModalOpen(false)}
              onSign={async () => {
                setSignatureModalOpen(false);
                const session = getSessionFromCookie();
                const token = session?.getAccessToken().getJwtToken();
                if (selectedUserCompany?.companyNumber && token) {
                  dispatch(fetchPortfolioServices({ companyNumber: selectedUserCompany.companyNumber, token }) as never);
                }
              }}
              signerName={`${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "JOHN DOE"}
              pdfUrls={signatureModalData.pdfUrls}
              ean={`${signatureModalData.ean}`}
              companyId={signatureModalData.companyId ?? 0}
              direction={signatureModalData.direction}
              billingInfo={signatureModalData.billingInfo}
              contractError={signatureModalData.contractError}
            />
          )}
          {pvModalOpen && pvContractData && (
            <ContractPreviewModal
              isOpen={pvModalOpen}
              onClose={() => setPvModalOpen(false)}
              onAccept={handlePvCurtailmentSign}
              contractData={pvContractData}
              signerName={`${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "JOHN DOE"}
              pdfUrl={pvPdfUrl}
              hasCombinedServices={pvHasCombinedServices}
              signBlockedReason={pvSignBlockedReason}
            />
          )}
          {bessModalOpen && bessContractData && (
            <BessContractPreviewModal
              isOpen={bessModalOpen}
              onClose={() => setBessModalOpen(false)}
              onAccept={handleBessSign}
              contractData={bessContractData}
              signerName={`${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "JOHN DOE"}
              pdfUrl={bessPdfUrl}
              hasCombinedServices={bessHasCombinedServices}
              signBlockedReason={bessSignBlockedReason}
              hasCertifiedController={bessHasCertifiedController}
            />
          )}
          {sendProposalContractId !== null && (
            <SendProposalModal
              isOpen={true}
              onClose={() => setSendProposalContractId(null)}
              target={{ kind: "flex", apiPath: "site-steering", contractId: sendProposalContractId }}
            />
          )}
          {bessSendProposalContractId !== null && (
            <SendProposalModal
              isOpen={true}
              onClose={() => setBessSendProposalContractId(null)}
              target={{ kind: "flex", apiPath: "bess", contractId: bessSendProposalContractId }}
            />
          )}
          {commodityProposal !== null && selectedUserCompany?.company_id != null && (
            <SendProposalModal
              isOpen={true}
              onClose={() => setCommodityProposal(null)}
              target={{
                kind: "commodity",
                ean: commodityProposal.ean,
                companyId: selectedUserCompany.company_id,
                direction: commodityProposal.direction,
              }}
            />
          )}
          {bessResignActive && (
            <BessResignContractModal
              isOpen
              pendingRow={bessResignActive.row}
              pdfUrl={bessResignActive.pdfUrl}
              initialBillingInfo={bessResignActive.billingInfo}
              projectedStartDate={bessResignActive.projectedStartDate}
              signerName={bessResignSignerName}
              onClose={() => setBessResignActive(null)}
              onSignSuccess={handleBessResignSuccess}
            />
          )}
        </div>
        {selectedService && (
          <ServiceDetailsModal
            service={selectedService}
            statusEntry={sdpStatuses.get(getStatusKey(selectedService) ?? "")}
            direction={
              isInjectionService(selectedService)
                ? "Injection"
                : isOfftakeService(selectedService)
                  ? "Offtake"
                  : undefined
            }
            onClose={() => setSelectedService(null)}
          />
        )}
      </div>
    </div>
  );
};

export default PortfolioServices;
